'use client';

export const dynamic = 'force-dynamic';

import { useState, useEffect, useRef } from 'react';
import Image from "next/image";
// import Link from "next/link";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { FeaturesSection } from "@/components/features-section";
import { PricingSection } from "@/components/pricing-section";
import { TestimonialSection } from "@/components/testimonial-section";
import { CTASection } from "@/components/cta-section";
import { FAQSection } from "@/components/faq-section";
import HomeHeader from "@/components/HomeHeader";
import Footer from "@/components/Footer";
import { useAuth } from "@/lib/auth/auth-context";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';



const rotatingPhrases = [
  { first: 'CLIENTS', second: 'FOCUS' },
  { first: 'EVENTS', second: 'MIND' },
  { first: 'WEDDINGS', second: 'WEEKEND' }
];

// Animated Text Component with Scramble Effect
const AnimatedWord = ({ word }: { word: string }) => {
  const [displayText, setDisplayText] = useState(word);
  const scrambleChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  
  useEffect(() => {
    const timeouts: NodeJS.Timeout[] = [];
    
    // Start scramble effect
    const scrambleDuration = 800; // Total scramble time
    const iterations = 20; // Number of scramble iterations
    
    for (let i = 0; i < iterations; i++) {
      const timeout = setTimeout(() => {
        if (i < iterations - 1) {
          // Scramble phase
          const scrambled = word
            .split('')
            .map((char, index) => {
              if (char === ' ') return ' ';
              // Gradually reveal characters from left to right
              const revealProgress = i / (iterations - 1);
              const shouldReveal = index / word.length < revealProgress;
              return shouldReveal 
                ? char 
                : scrambleChars[Math.floor(Math.random() * scrambleChars.length)];
            })
            .join('');
          setDisplayText(scrambled);
        } else {
          // Final reveal
          setDisplayText(word);
        }
      }, (i * scrambleDuration) / iterations);
      
      timeouts.push(timeout);
    }
    
    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [word]);
  
  return (
    <span className="inline-block text-green-600 font-mono">
      {displayText}
    </span>
  );
};

export default function Home() {
  const router = useRouter();
  const { user } = useAuth();
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const heroImageRef = useRef<HTMLDivElement>(null);
  const heroTextRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const testimonialsRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhraseIndex((prevIndex) => (prevIndex + 1) % rotatingPhrases.length);
    }, 3000); // Change every 3 seconds
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);

    // Animate header sliding down from top
    if (headerRef.current) {
      gsap.set(headerRef.current, {
        y: -100,
        opacity: 0
      });

      gsap.to(headerRef.current, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        delay: 0,
        ease: "power2.out"
      });
    }

    // Animate hero text elements
    if (heroTextRef.current) {
      const elements = heroTextRef.current.children;
      
      // Set initial state for all elements
      gsap.set(elements, {
        y: 50,
        opacity: 0
      });

      // Animate elements with staggered delays
      gsap.to(elements, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.1,
        delay: 0.4,
        ease: "power2.out"
      });
    }

    // Animate hero image
    if (heroImageRef.current) {
      // Set initial state - image starts from below and is invisible
      gsap.set(heroImageRef.current, {
        y: 100,
        opacity: 0,
        scale: 0.95
      });

      // Animate image sliding up with delay
      gsap.to(heroImageRef.current, {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 1.2,
        delay: 1.0,
        ease: "power3.out"
      });
    }

    // Scroll-triggered animations for sections
    const sections = [
      { ref: featuresRef, name: "Features" },
      { ref: testimonialsRef, name: "Testimonials" },
      { ref: pricingRef, name: "Pricing" },
      { ref: ctaRef, name: "CTA" },
    ];

    sections.forEach((section) => {
      if (section.ref.current) {
        gsap.set(section.ref.current, {
          y: 80,
          opacity: 1
        });

        gsap.to(section.ref.current, {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: section.ref.current,
            start: "top 80%",
            toggleActions: "play none none none"
          }
        });
      }
    });

    // Cleanup function to kill ScrollTriggers
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const handleButtonClick = () => {
    if (user) {
      router.push('/dashboard');
    } else {
      router.push('/signup');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <div ref={headerRef}>
        <HomeHeader />
      </div>
      
      {/* Hero Section */}
      <div className="relative w-full flex-1 flex flex-col items-center justify-center overflow-hidden">

        {/* Hero Background - Gradient + Pattern */}
        {/* <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-background z-0 overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ 
            backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(var(--primary)) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div> */}
        
        {/* Content */}
        <div className="relative z-10 container max-w-7xl mx-auto px-6 py-20 flex flex-col items-center text-center">
        {/* <div className="flex items-center mb-4">
          <div className="w-32 h-32 relative">
            <Image
              src="https://rgvfkcfnfbtqyjlrvynz.supabase.co/storage/v1/object/public/vendor-assets/vendor-assets/logo.png"
              alt="Logo"
              fill
              sizes="(max-width: 768px) 100vw, 256px"
              className="object-contain dark:invert"
              priority
            />
          </div>
        </div> */}
          <div ref={heroTextRef}>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-normal tracking-tight mb-6">
              Take On More <AnimatedWord word={rotatingPhrases[currentPhraseIndex].first} />
            </h1>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-normal tracking-tight mb-6">
              Without Losing Your <AnimatedWord word={rotatingPhrases[currentPhraseIndex].second} />
            </h1>
            
            <p className="text-lg md:text-xl font-light text-muted-foreground max-w-3xl mb-6">
              Everything you need to manage, grow, and breathe easier.
            </p>
            
            {/* <div className="bg-white dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-full px-6 py-3 mb-6">
              <p className="text-green-700 dark:text-green-300 font-medium text-lg">
                Start your 7 days free trial today!
              </p>
            </div>
            
            <p className="text-gray-600 dark:text-gray-400 text-center mb-10">
              Try Menumate free for 7 days, no credit card required.
            </p> */}
            
            <div className="flex flex-col sm:flex-row gap-4 w-full justify-center mt-6">
              <Button size="lg" variant={'default'} className="px-8 rounded-full bg-green-600 hover:bg-green-600/90" onClick={handleButtonClick}>
                {user ? 'Go to application' : 'Start for free'}
              </Button>
            </div>
            <p className="text-lg md:text-xs font-light text-muted-foreground max-w-3xl mt-6">
              {user ? "You're signed in" : "Try menumate free for 7 days, no credit card required."}
            </p>
          </div>
          
          {/* Dashboard Image */}
          <div ref={heroImageRef} className="mt-16 mx-auto">
            <Image
              src="https://rgvfkcfnfbtqyjlrvynz.supabase.co/storage/v1/object/public/vendor-assets/vendor-assets/hero1.png"
              alt="Dashboard Preview"
              width={1200}
              height={800}
              className="w-full h-auto"
              priority
            />
          </div>

          <div ref={featuresRef}>
            <FeaturesSection />
          </div>
        </div>
      </div>

      {/* Testimonial Section */}
      <div ref={testimonialsRef}>
        <TestimonialSection />
      </div>

      {/* Pricing Section */}
      <div ref={pricingRef}>
        <PricingSection />
      </div>
      
      {/* CTA Section */}
      <div ref={ctaRef}>
        <CTASection />
      </div>
      
      {/* FAQ Section */}
      <FAQSection />
      
      {/* Footer */}

        <Footer />

    </div>
  );
}
